import { loadShips } from "../../data/loadShips";
import { getSystem } from "../../data/loadSystems";
import type { NpcHullTier, PlayerShip } from "../../types";
import { triggerSave } from "../persistence";
import { state } from "../state";
import type { ScreenContext } from "../app";
import { renderHeader } from "../components";
import { startingFuelCapacity } from "../../fuel";
import { startingProvisionsCapacity } from "../../provisions";
import { effectiveNow } from "../../gameClock";
import { hourlyUpkeepCr } from "../../shipUpkeep";
import { maxFleetSize } from "../../captainSkills";
import { describeDock, systemOfDockKey } from "../../shipStorage";
import { shipIconHtml } from "../shipIcon";

// Escorts = every fleet entry except the hull currently being flown
// (which shipSelectScreen pushes into state.fleet as an "owned" entry).
// Command governs escort count, never the flagship.
function escortsInFleet(): number {
  return state.fleet.filter((f) => f.id !== state.currentShipInstanceId).length;
}

// Shipyard -- design doc Section 12's "Shipyard -- New Hull" browsing
// screen (LOCKED — Phase 2 wireframe): Class tabs -> Size filter -> list
// of variant cards with View/Buy, distinct from the Ship Card it's
// supposed to lead into once a hull is bought.
//
// What's real: the full 96-ship fixed roster (16.16) with real per-ship
// costCr, filtered by real role ("Class") and hullTier ("Size"). Buy is a
// real credits-gated purchase -- disabled unless state.credits >= costCr
// and the current System has a real Station (Section 12: "only faction
// hub systems have stations," Stations always have a Shipyard built in).
//
// What's NOT built, and why this isn't the full doc spec:
// - **No Ship Card.** The doc's Ship Card is a full module-fitting editor
//   (open slots capped by category, a crew stepper per slot, Hire Max
//   Crew assigning every module its own crew, live per-module
//   effectiveness stats). None of that exists anywhere in the data model
//   -- PlayerShip is a fixed aggregate-stat roster entry (hullHp, dps,
//   crew as one total number, etc.), not a hull-plus-fitted-modules
//   structure (see types.ts's own comment: "no build-your-own fitting
//   system"). Buy here goes straight from the browsing list to owning the
//   ship, with no intermediate fitting step, since there's nothing to fit.
// - **View opens the real Ship Card (16.41), not a hull-art popup.** The
//   doc's View just shows hull artwork with "no other function" -- no art
//   assets exist for any of the 96 ships, so this substitutes the Ship
//   Card's real stat grid instead, with instanceId null (not owned yet,
//   no weapon-upgrade levels exist to show).
// - **No Sell, no Module Bay, no resale value.** Selling an owned hull for
//   30-40% refund + stripped modules into a Base's Module Bay needs both
//   a fitting system (see above) and a real Base system (neither exists).
// Buying adds the hull to the real fleet (state.fleet, see state.ts) as
// an "owned" entry -- non-destructive, unlike this screen's earlier
// version which replaced currentShip outright. currentShip only
// auto-updates on a captain's very first purchase (so a brand-new fleet
// isn't empty-handed); afterward, switching which owned/hired ship is
// actively flown happens from the Fleet roster's "Fly this ship" action.

const ships = loadShips();
const ROLES = [...new Set(ships.map((s) => s.role))];
const TIERS: NpcHullTier[] = ["T1", "T2", "T3", "T4"];

export function renderShipyardScreen(container: HTMLElement, ctx: ScreenContext): void {
  let roleFilter: string | "All" = "All";
  let tierFilter: NpcHullTier | "All" = "All";

  renderHeader(container, ctx);

  const wrap = document.createElement("div");
  container.appendChild(wrap);

  function render(): void {
    const system = getSystem(state.currentSystemId);
    const hasStation = system?.hasStation ?? false;
    const filtered = ships.filter(
      (s) => (roleFilter === "All" || s.role === roleFilter) && (tierFilter === "All" || s.hullTier === tierFilter)
    );

    wrap.innerHTML = `
      <h1>Shipyard</h1>
      <p class="dim">${ships.length} hulls in the fixed roster (16.16) -- 6 classes x 4 sizes x 4 variants. ${state.credits.toLocaleString()} CR available.</p>
      ${
        hasStation
          ? `<p class="dim">Docked at ${system!.name} Station -- Buy available.</p>`
          : `<p class="dim">${system ? system.name : state.currentSystemId} has no Station -- travel to a hub system to buy a hull.</p>`
      }

      ${ownedRosterHtml()}

      <h2>Buy or hire a hull</h2>
      <div class="filter-row">
        <label>
          Class
          <select data-filter="role">
            <option value="All" ${roleFilter === "All" ? "selected" : ""}>All</option>
            ${ROLES.map((r) => `<option value="${r}" ${roleFilter === r ? "selected" : ""}>${r}</option>`).join("")}
          </select>
        </label>
        <label>
          Size
          <select data-filter="tier">
            <option value="All" ${tierFilter === "All" ? "selected" : ""}>All</option>
            ${TIERS.map((t) => `<option value="${t}" ${tierFilter === t ? "selected" : ""}>${t}</option>`).join("")}
          </select>
        </label>
      </div>

      <div class="ship-list">
        ${filtered.map((s) => shipyardCardHtml(s, hasStation)).join("")}
      </div>
    `;

    wrap.querySelector<HTMLSelectElement>('select[data-filter="role"]')!.addEventListener("change", (e) => {
      roleFilter = (e.target as HTMLSelectElement).value;
      render();
    });
    wrap.querySelector<HTMLSelectElement>('select[data-filter="tier"]')!.addEventListener("change", (e) => {
      tierFilter = (e.target as HTMLSelectElement).value as NpcHullTier | "All";
      render();
    });

    wrap.querySelectorAll<HTMLButtonElement>("button[data-buy]").forEach((btn) => {
      btn.addEventListener("click", () => {
        const name = btn.dataset.name!;
        const variant = btn.dataset.variant!;
        buyShip(name, variant);
      });
    });
    wrap.querySelectorAll<HTMLButtonElement>("button[data-hire]").forEach((btn) => {
      btn.addEventListener("click", () => {
        hireShip(btn.dataset.name!, btn.dataset.variant!);
      });
    });

    // Waypointing a stored ship reuses the Space Map's own existing
    // waypoint mechanic (state.waypointSystemId) rather than inventing a
    // second way to set a course -- the route line, hop count and travel
    // all keep working exactly as they already do.
    wrap.querySelectorAll<HTMLButtonElement>("button[data-waypoint]").forEach((btn) => {
      btn.addEventListener("click", () => {
        const systemId = btn.dataset.waypoint!;
        state.waypointSystemId = systemId;
        state.lastEvent = `Course set for ${getSystem(systemId)?.name ?? systemId} — see the Space Map.`;
        triggerSave(ctx);
        render();
      });
    });
    wrap.querySelectorAll<HTMLButtonElement>("button[data-view]").forEach((btn) => {
      btn.addEventListener("click", () => {
        const ship = ships.find((s) => s.name === btn.dataset.name && s.variant === btn.dataset.variant);
        if (!ship) return;
        state.shipCardTarget = { ship, instanceId: null };
        ctx.navigate("shipCard");
      });
    });
  }

  // Hiring moved here in 16.76, when the Captain's Lounge was repurposed
  // into the captain's own record screen and captain recruitment was cut
  // entirely (captains come with their ships). A hired
  // ship arrives already crewed and captained -- there is no separate
  // captain to recruit -- and can never be flown by the player (16.72),
  // only commanded as an escort.
  function hireShip(name: string, variant: string): void {
    const system = getSystem(state.currentSystemId);
    if (!system?.hasStation) return;
    const ship = ships.find((s) => s.name === name && s.variant === variant);
    if (!ship) return;

    const capacity = maxFleetSize(state.captainSkills.Command);
    if (escortsInFleet() >= capacity) {
      state.lastEvent = `Escort capacity reached (${capacity}) -- raise Command skill to field more.`;
      render();
      return;
    }

    state.fleet.push({
      id: crypto.randomUUID(),
      ship,
      source: "hired",
      stance: "Active",
      lastUpkeepAt: effectiveNow(state.clock, Date.now())
    });
    state.lastEvent = `Hired ${ship.name} (${ship.variant}) -- ${Math.round(hourlyUpkeepCr(ship)).toLocaleString()} CR/hour upkeep starts now.`;
    triggerSave(ctx);
    render();
  }

  function buyShip(name: string, variant: string): void {
    const system = getSystem(state.currentSystemId);
    if (!system?.hasStation) return;
    const ship = ships.find((s) => s.name === name && s.variant === variant);
    if (!ship || state.credits < ship.costCr) return;

    state.credits -= ship.costCr;
    const fleetId = crypto.randomUUID();
    state.fleet.push({ id: fleetId, ship, source: "owned", stance: "Active" });
    if (!state.currentShip) {
      state.currentShip = ship;
      state.currentShipInstanceId = fleetId; // reuse the same instance id as its fleet entry
      state.fuelCapacity = startingFuelCapacity(ship.hullTierLabel);
      state.fuelOnBoard = state.fuelCapacity;
      state.provisionsCapacity = startingProvisionsCapacity(ship.crew);
      state.provisionsOnBoard = state.provisionsCapacity;
      state.provisionsLastUpdatedAt = effectiveNow(state.clock, Date.now());
      state.isLoanerShip = false;
      state.inEscapePod = false; // a real bought ship ends any Escape Pod segment (16.62)
    }
    state.lastEvent = `Bought ${ship.name} (${ship.variant}) for ${ship.costCr.toLocaleString()} CR.`;
    triggerSave(ctx);
    ctx.rerender();
  }

  render();
}

// "Your Ships" (16.72) -- every hull the captain owns and where it
// actually is, so a fleet scattered across the galaxy is legible instead
// of being a list of names with no geography. Ships parked elsewhere get
// a waypoint action; the one you're flying is called out as such.
//
// Hired ships are deliberately excluded: they're escorts that travel with
// you, never stored, and can't be flown (16.72).
function ownedRosterHtml(): string {
  const owned = state.fleet.filter((f) => f.source === "owned");
  if (owned.length === 0) {
    return `<h2>Your Ships</h2><p class="dim">You don't own any hulls yet.</p>`;
  }

  const rows = owned
    .map((f) => {
      const flying = f.id === state.currentShipInstanceId;
      const where = describeDock(f.dockedAtKey, (id) => getSystem(id)?.name);
      const dockSystem = f.dockedAtKey ? systemOfDockKey(f.dockedAtKey) : null;
      const elsewhere = !flying && dockSystem && dockSystem !== state.currentSystemId;
      return `
        <div class="cargo-row">
          <label style="display:flex; align-items:center; gap:0.5rem;">
            ${shipIconHtml(f.ship, "sm")}
            <span class="cargo-row-name">${f.ship.name}</span>
            <span class="pill">${f.ship.variant}</span>
            ${flying ? '<span class="pill">flying</span>' : ""}
          </label>
          <span class="dim">${flying ? "with you" : where}</span>
          ${
            elsewhere
              ? `<button type="button" class="btn-secondary" data-waypoint="${dockSystem}">Set course</button>`
              : `<span class="dim">${flying ? "" : "here"}</span>`
          }
        </div>
      `;
    })
    .join("");

  return `
    <h2>Your Ships</h2>
    <p class="dim">
      ${owned.length} owned hull${owned.length === 1 ? "" : "s"}. A ship stays wherever you left it --
      travel there (your pod is safe and fast) to board it from the Fleet Roster.
    </p>
    <div class="cargo-list">${rows}</div>
  `;
}

function shipyardCardHtml(s: PlayerShip, hasStation: boolean): string {
  const ehp = s.hullHp + s.shieldHp;
  const ownedCount = state.fleet.filter((f) => f.ship.name === s.name && f.ship.variant === s.variant).length;
  const canAfford = state.credits >= s.costCr;
  const atEscortCapacity = escortsInFleet() >= maxFleetSize(state.captainSkills.Command);
  return `
    <div class="ship-card">
      <div class="ship-card-title" style="display:flex; align-items:center; gap:0.55rem;">
        ${shipIconHtml(s, "md")}
        <strong>${s.name}</strong> <span class="pill">${s.variant}</span> <span class="pill">${s.hullTier}</span>
        ${ownedCount > 0 ? `<span class="pill tag-solo">owned x${ownedCount}</span>` : ""}
      </div>
      <div class="ship-card-stats dim">
        EHP ${ehp} &middot; DPS ${s.dps} &middot; Cargo ${s.cargoM3} m3 · Ore ${s.oreHoldM3} m3 &middot; Crew ${s.crew} &middot; ${s.costCr.toLocaleString()} CR
      </div>
      <div class="ship-card-stats dim">
        Hire: free up front, ${Math.round(hourlyUpkeepCr(s)).toLocaleString()} CR/hour &middot; comes crewed, cannot be flown by you
      </div>
      <div class="cargo-actions">
        <span class="dim">${!hasStation ? "No Station here" : !canAfford ? "Not enough credits to buy" : ""}</span>
        <div style="display:flex; gap:0.5rem;">
          <button type="button" class="btn-secondary" data-view data-name="${s.name}" data-variant="${s.variant}">View</button>
          <button type="button" class="btn-secondary" data-hire data-name="${s.name}" data-variant="${s.variant}" ${hasStation && !atEscortCapacity ? "" : "disabled"}>Hire</button>
          <button type="button" data-buy data-name="${s.name}" data-variant="${s.variant}" ${hasStation && canAfford ? "" : "disabled"}>Buy</button>
        </div>
      </div>
    </div>
  `;
}
